// An event (/events/:slug) — Secret Sample: join, maybe put a seed in the
// pool, and get handed someone else's to flip.
//
//   signed out / not in   what it is, when, and one button: Join
//   Join                  an account if you need one, then JoinFlow: a loop
//                         for the pool (or just play), then the Discord call
//   in, waiting           the sealed record ("?") and your entry ticket
//   assigned              the record slides out: the exact version you got
//   closed                the event has ended
//
// While you're waiting on a seed the page polls, so the record comes out on
// its own when the admin hits Assign.

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Monitor, Shuffle } from "lucide-react";
import type { ApiEventView } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { ApiError } from "@web-shared/lib/api";
import { json, webApi } from "@web-shared/lib/web-api";
import { eventPath } from "@/lib/ctas";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import {
  AssignmentDetails,
  EntryTicket,
  JoinFlow,
  PooledRow,
  PoolPicker,
  StatusBadge,
  VinylReveal,
  whenOf,
  type JoinStep,
} from "../components/EventParts";

const WAITING_POLL_MS = 5_000;

/** Where the upload page sends you back with: which step of Join to resume, what happened to the pool. */
export interface EventReturnState {
  joinStep?: JoinStep;
  poolProblem?: string;
}

export function useEvent(slug: string) {
  const session = useSession();
  return useQuery({
    queryKey: ["web", "event", slug, session?.user.id ?? ""],
    queryFn: () => webApi<ApiEventView>(`/events/${encodeURIComponent(slug)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    // Joined and still without a seed: watch for Assign.
    refetchInterval: (q) => {
      const v = q.state.data;
      return v && v.event.status !== "closed" && v.me?.joined && !v.me.assignment ? WAITING_POLL_MS : false;
    },
    // People wait on the call in Discord, with this tab behind it.
    refetchIntervalInBackground: true,
  });
}

export function EventPage() {
  const { slug = "" } = useParams();
  const event = useEvent(slug);
  useDocumentTitle(event.data?.event.title ?? "Event");
  const location = useLocation();
  const navigate = useNavigate();
  const returned = (location.state ?? null) as EventReturnState | null;
  const [step, setStep] = useState<JoinStep | null>(null);
  const [poolProblem, setPoolProblem] = useState<string | null>(null);

  // Back from uploading a seed for the pool: pick Join up where it left off.
  useEffect(() => {
    if (!returned) return;
    if (returned.joinStep) setStep(returned.joinStep);
    if (returned.poolProblem) setPoolProblem(returned.poolProblem);
    navigate(location.pathname, { replace: true, state: null });
  }, [returned, navigate, location.pathname]);

  if (event.isPending) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 pt-6">
        <AmbientGlow />
        <Skeleton className="mx-auto h-12 w-2/3 rounded-xl" />
        <Skeleton className="h-56 w-full rounded-2xl" />
      </div>
    );
  }
  if (event.isError) {
    const missing = event.error instanceof ApiError && event.error.status === 404;
    return (
      <div className="py-16 text-center">
        <AmbientGlow />
        <p className="text-lg font-bold">{missing ? "No such event." : "Couldn't load this event."}</p>
        <Button asChild variant="outline" className="mt-4 rounded-full">
          <Link to="/events">See all events</Link>
        </Button>
      </div>
    );
  }

  const v = event.data;
  return (
    <div className="mx-auto max-w-2xl space-y-8 pb-10">
      <AmbientGlow />
      <EventHero view={v} />
      <EventBody slug={slug} view={v} onJoined={() => setStep("pool")} poolProblem={poolProblem} />
      <JoinFlow slug={slug} view={v} step={step} onStep={setStep} onClose={() => setStep(null)} />
      {v.me?.isAdmin && (
        <div className="text-center">
          <Link to={`${eventPath(slug)}/admin`} className="text-sm text-muted-foreground hover:text-foreground">
            Run this event →
          </Link>
        </div>
      )}
    </div>
  );
}

function EventHero({ view }: { view: ApiEventView }) {
  const { event } = view;
  const counts = [
    view.participantCount > 0 ? `${view.participantCount} ${view.participantCount === 1 ? "producer" : "producers"} in` : null,
    view.poolCount > 0 ? `${view.poolCount} ${view.poolCount === 1 ? "seed" : "seeds"} in the pool` : null,
  ].filter(Boolean);
  return (
    <header className="space-y-3 pt-4 text-center sm:pt-8">
      <div className="flex items-center justify-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-primary">
          <Shuffle className="size-3.5" /> Secret Sample
        </span>
        {event.status !== "open" && <StatusBadge status={event.status} />}
      </div>
      <h1 className="text-5xl font-black tracking-tighter sm:text-6xl">{event.title}</h1>
      {event.startsAt && <p className="text-sm font-semibold">{whenOf(event.startsAt)}</p>}
      {event.blurb && (
        <p className="mx-auto max-w-prose whitespace-pre-line text-base leading-relaxed text-muted-foreground">{event.blurb}</p>
      )}
      {counts.length > 0 && <p className="text-xs text-muted-foreground">{counts.join(" · ")}</p>}
    </header>
  );
}

function EventBody({
  slug,
  view,
  onJoined,
  poolProblem,
}: {
  slug: string;
  view: ApiEventView;
  onJoined: () => void;
  poolProblem: string | null;
}) {
  const { event, me } = view;
  if (event.status === "closed") {
    return (
      <section className="space-y-6 text-center">
        {me?.assignment && <VinylReveal seed={me.assignment} eventTitle={event.title} />}
        <div>
          <p className="text-xl font-black">This one's over.</p>
          <p className="mt-1 text-sm text-muted-foreground">Thanks for flipping. Catch the next one on Discord.</p>
        </div>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/events">All events</Link>
        </Button>
      </section>
    );
  }
  if (!me?.joined) return <JoinButton slug={slug} onJoined={onJoined} />;

  return (
    <div className="space-y-8">
      <section className="space-y-5">
        <VinylReveal seed={me.assignment} eventTitle={event.title} />
        {me.assignment ? (
          <AssignmentDetails seed={me.assignment} />
        ) : (
          <div className="text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-primary">Your Secret Sample</p>
            <p className="mt-1 text-sm text-muted-foreground">Sealed until the draw. It comes out right here, live.</p>
          </div>
        )}
      </section>
      <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground sm:hidden">
        <Monitor className="size-3.5" />
        Flipping needs a desktop browser. You're in either way.
      </p>
      <Ticket slug={slug} view={view} poolProblem={poolProblem} />
    </div>
  );
}

function JoinButton({ slug, onJoined }: { slug: string; onJoined: () => void }) {
  const gate = useAuthGate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = () =>
    gate.requireSignIn(
      "event_join",
      "Make an account to join Secret Sample",
      () => {
        setBusy(true);
        setError(null);
        webApi(`/events/${encodeURIComponent(slug)}/join`, json("POST"))
          .then(async () => {
            await queryClient.invalidateQueries({ queryKey: ["web", "event", slug] });
            onJoined();
          })
          .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't join."))
          .finally(() => setBusy(false));
      },
      "signup",
    );

  return (
    <div className="flex flex-col items-center gap-3">
      <Button
        onClick={join}
        disabled={busy}
        className="h-14 w-full max-w-xs rounded-full text-lg font-black shadow-[0_0_50px_-12px_var(--primary)]"
      >
        {busy ? <Loader2 className="size-5 animate-spin" /> : "Join"}
      </Button>
      {error && <p className="text-sm text-removed">{error}</p>}
    </div>
  );
}

function Ticket({ slug, view, poolProblem }: { slug: string; view: ApiEventView; poolProblem: string | null }) {
  const queryClient = useQueryClient();
  const open = view.event.status === "open";
  const pooled = view.me?.pooled ?? [];
  const admin = view.me?.isAdmin ?? false;
  const canAdd = admin || (open ? true : pooled.length === 0);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(poolProblem);
  useEffect(() => setError(poolProblem), [poolProblem]);

  const remove = (poolItemId: string) => {
    setBusy(true);
    setError(null);
    webApi(`/events/${encodeURIComponent(slug)}/pool?item=${encodeURIComponent(poolItemId)}`, json("DELETE"))
      .then(() => queryClient.invalidateQueries({ queryKey: ["web", "event", slug] }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't take it out."))
      .finally(() => setBusy(false));
  };

  const discord = view.event.discordUrl;
  return (
    <EntryTicket
      title="You're in"
      sub={view.me?.assignment ? "Flip it, then play it back on the call." : "See you on the call."}
      aside={
        discord ? (
          <a
            href={discord}
            target="_blank"
            rel="noopener"
            className="hidden shrink-0 items-center gap-1.5 rounded-full bg-[#5865F2]/15 px-3 py-1.5 text-xs font-bold text-[#a5adff] ring-1 ring-[#5865F2]/40 transition-colors hover:bg-[#5865F2]/25 sm:inline-flex"
          >
            Discord <ExternalLink className="size-3" />
          </a>
        ) : undefined
      }
      stub={
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Your loop in the pool</p>
            {canAdd && !picking && pooled.length > 0 && (
              <button type="button" onClick={() => setPicking(true)} className="text-xs font-semibold text-muted-foreground hover:text-foreground">
                {admin ? "Add another" : "Change"}
              </button>
            )}
          </div>
          {pooled.map((p) => (
            <PooledRow key={p.poolItemId} seed={p} busy={busy} onRemove={open ? () => remove(p.poolItemId) : undefined} />
          ))}
          {pooled.length === 0 && !picking && (
            canAdd ? (
              <button
                type="button"
                onClick={() => setPicking(true)}
                className="w-full rounded-xl border border-dashed border-white/15 px-4 py-4 text-left text-sm transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
              >
                <span className="font-semibold">Add a loop</span>
                <span className="block text-muted-foreground">Optional. Someone else flips it — never you.</span>
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">You're playing without one.</p>
            )
          )}
          {picking && (
            <div className="space-y-2">
              <PoolPicker slug={slug} pooled={pooled} onPooled={() => setPicking(false)} />
              <button type="button" onClick={() => setPicking(false)} className="text-xs text-muted-foreground hover:text-foreground">
                Cancel
              </button>
            </div>
          )}
          {discord && (
            <a
              href={discord}
              target="_blank"
              rel="noopener"
              className="flex items-center justify-center gap-1.5 pt-1 text-xs font-semibold text-muted-foreground hover:text-foreground sm:hidden"
            >
              Join the call on Discord <ExternalLink className="size-3" />
            </a>
          )}
          {error && <p className="text-sm text-removed">{error}</p>}
        </div>
      }
    />
  );
}
